import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Palette } from 'src/feature/theme/model/Palette'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { useMyTeamStore } from '../../store/my-team.store'
import { CriticalGapChart } from '../molecules/CriticalGapChart'
import { DonutChart } from '../molecules/DonutChart'
import { HorizontalBarChart } from '../molecules/HorizontalBarChart'
import { PageMetrics } from '../molecules/PageMetrics'
import { TeamUnusedSkills } from '../molecules/TeamUnusedSkills'
import { TeamUsedSkills } from '../molecules/TeamUsedSkills'

// Constants
const ICON_UP = 'ArrowUpwardRoundedIcon'
const ICON_DOWN = 'ArrowDownwardRoundedIcon'
const ICON_COLOR_UP = 'secondary.main'
const ICON_COLOR_DOWN = Palette.DANGER

// Helper functions
function formatRatingValue(name?: string): string {
  return name || '-'
}

export const TeamDashboardInsights = observer(() => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const navigate = useNavigate()
  const { isLoading: dashboardLoading } = myTeamStore.getGenericFieldsState('getTeamDashboardMetrics')
  const { isLoading: skillGapLoading } = myTeamStore.getGenericFieldsState('getTeamDashboardSkillGapMetrics')
  const { isLoading: teamMetricsLoading } = myTeamStore.getGenericFieldsState('getTeamDashboardTeamMetrics')
  const { isLoading: skillGapPerformanceLoading } = myTeamStore.getGenericFieldsState('getSkillGapPerformance')
  const { isLoading: skillsByRatingLoading } = myTeamStore.getGenericFieldsState('getTeamDashboardSkillsByRating')

  const pageTranslation = translation.pages.myTeam

  const handleSkillClick = useCallback((skillName: string) => {
    navigate(appRoutePath.myTeamSkillGaps(skillName))
  }, [navigate])

  useEffect(() => {
    myTeamStore.getTeamDashboardMetrics()
    myTeamStore.getSkillGapPerformance(50)
    myTeamStore.getTeamDashboardSkillsByRating(3)
    myTeamStore.getTeamDashboardSkillGapMetrics()
    myTeamStore.getTeamDashboardTeamMetrics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dashboardMetrics = [
    { label: pageTranslation.metricsHeader.totalUsers(), value: myTeamStore.teamDashboardMetrics?.totalUsers ?? '-' },
    { label: pageTranslation.metricsHeader.totalTeams(), value: myTeamStore.teamDashboardMetrics?.totalTeams ?? '-' },
    { label: pageTranslation.metricsHeader.totalRoles(), value: myTeamStore.teamDashboardMetrics?.totalRoles ?? '-' },
  ]

  const skillGapMetrics = [
    { label: pageTranslation.metricsHeader.usedSkills(), value: myTeamStore.teamDashboardSkillGapMetrics?.usedSkills ?? '-' },
    { label: pageTranslation.metricsHeader.unusedSkills(), value: myTeamStore.teamDashboardSkillGapMetrics?.unusedSkills ?? '-' },
    { label: pageTranslation.metricsHeader.avgSkillRating(), value: myTeamStore.teamDashboardSkillGapMetrics?.avgSkillRating.toFixed(2) ?? '-' },
  ]

  const skillGapPerformanceMetrics = [
    {
      label: pageTranslation.metricsHeader.criticalGaps(),
      value: myTeamStore.skillGapPerformance?.criticalGaps !== undefined ? `${myTeamStore.skillGapPerformance.criticalGaps}%` : '-',
      tootlTipDesc: pageTranslation.metricsHeader.criticalGapsTooltip(),
    },
    {
      label: pageTranslation.metricsHeader.usersWithGaps(),
      value: myTeamStore.skillGapPerformance?.usersWithGaps !== undefined ? `${myTeamStore.skillGapPerformance.usersWithGaps}%` : '-',
      tootlTipDesc: pageTranslation.metricsHeader.usersWithGapsTooltip(),
    },
    {
      label: pageTranslation.metricsHeader.meetsExpectations(),
      value: myTeamStore.skillGapPerformance?.meetsExceedsExpectation !== undefined ? `${myTeamStore.skillGapPerformance.meetsExceedsExpectation}%` : '-',
      valueColor: true,
      tootlTipDesc: pageTranslation.metricsHeader.meetsExpectationsTooltip(),
    },
    {
      label: pageTranslation.metricsHeader.belowExpectations(),
      value: myTeamStore.skillGapPerformance?.belowExpectation !== undefined ? `${myTeamStore.skillGapPerformance.belowExpectation}%` : '-',
      tootlTipDesc: pageTranslation.metricsHeader.belowExpectationsTooltip(),
    },
  ]

  const metrics = [
    ...dashboardMetrics,
    ...skillGapMetrics,
  ]

  const { teamDashboardTeamMetrics, teamDashboardSkillGapMetrics, skillGapPerformance } = myTeamStore
  const topTeam = teamDashboardTeamMetrics?.topRatedTeams?.[0]
  const topRole = teamDashboardSkillGapMetrics?.topRatedRoles?.[0]
  const topSkill = skillGapPerformance?.highRatedSkill?.[0]
  const bottomTeam = teamDashboardTeamMetrics?.lowestRatedTeams?.[0]
  const bottomRole = teamDashboardSkillGapMetrics?.lowestRatedRoles?.[0]
  const bottomSkill = skillGapPerformance?.lowRatedSkill?.[0]

  const topBottomMetrics = [
    {
      label: pageTranslation.metricsHeader.highestRatedTeams(),
      value: formatRatingValue(topTeam?.name),
      icon: ICON_UP,
      iconColor: ICON_COLOR_UP,
    },
    {
      label: pageTranslation.metricsHeader.highestRatedRoles(),
      value: formatRatingValue(topRole?.name),
      icon: ICON_UP,
      iconColor: ICON_COLOR_UP,
    },
    {
      label: pageTranslation.metricsHeader.highestRatedSkills(),
      value: formatRatingValue(topSkill?.name),
      icon: ICON_UP,
      iconColor: ICON_COLOR_UP,
    },
    {
      label: pageTranslation.metricsHeader.lowestRatedTeams(),
      value: formatRatingValue(bottomTeam?.name),
      icon: ICON_DOWN,
      iconColor: ICON_COLOR_DOWN,
    },
    {
      label: pageTranslation.metricsHeader.lowestRatedRoles(),
      value: formatRatingValue(bottomRole?.name),
      icon: ICON_DOWN,
      iconColor: ICON_COLOR_DOWN,
    },
    {
      label: pageTranslation.metricsHeader.lowestRatedSkills(),
      value: formatRatingValue(bottomSkill?.name),
      icon: ICON_DOWN,
      iconColor: ICON_COLOR_DOWN,
    },
  ]

  return (
    <Box sx={styles.root}>
      {/* Top Metrics */}
      <PageMetrics
        sx={styles.metricsGrid}
        metrics={metrics}
        isLoading={dashboardLoading || skillGapLoading}
        size="medium"
      />

      {/* Top/Bottom Teams, Roles, Skills */}
      <PageMetrics
        sx={styles.metricsGrid}
        metrics={topBottomMetrics}
        isLoading={teamMetricsLoading || skillGapLoading || skillGapPerformanceLoading}
        size="medium"
      />

      {/* Team/Role Ratings & Skills */}
      <AppBox sx={styles.section}>
        <AppBox sx={styles.chartRow}>
          <AppBox sx={styles.chartItem}>
            <HorizontalBarChart
              title={pageTranslation.metricsHeader.highestRatedTeams()}
              icon={ICON_UP}
              chartData={myTeamStore.teamDashboardTeamMetrics?.topRatedTeams}
              alternateData={myTeamStore.teamDashboardSkillGapMetrics?.topRatedRoles}
              alternateTitle={pageTranslation.metricsHeader.highestRatedRoles()}
              toggleLabels={{ primary: pageTranslation.metricsHeader.teams(), alternate: pageTranslation.metricsHeader.roles() }}
              isLoading={teamMetricsLoading || skillGapLoading}
            />
          </AppBox>
          <AppBox sx={styles.chartItem}>
            <HorizontalBarChart
              title={pageTranslation.metricsHeader.lowestRatedTeams()}
              icon={ICON_DOWN}
              chartData={myTeamStore.teamDashboardTeamMetrics?.lowestRatedTeams}
              alternateData={myTeamStore.teamDashboardSkillGapMetrics?.lowestRatedRoles}
              alternateTitle={pageTranslation.metricsHeader.lowestRatedRoles()}
              toggleLabels={{ primary: pageTranslation.metricsHeader.teams(), alternate: pageTranslation.metricsHeader.roles() }}
              isLoading={teamMetricsLoading || skillGapLoading}
            />
          </AppBox>
        </AppBox>
        <AppBox sx={styles.chartRow}>
          <AppBox sx={styles.chartItem}>
            <HorizontalBarChart
              title={pageTranslation.metricsHeader.highestRatedSkills()}
              icon={ICON_UP}
              chartData={myTeamStore.skillGapPerformance?.highRatedSkill || []}
              onItemClick={handleSkillClick}
              isLoading={skillGapPerformanceLoading}
            />
          </AppBox>
          <AppBox sx={styles.chartItem}>
            <HorizontalBarChart
              title={pageTranslation.metricsHeader.lowestRatedSkills()}
              icon={ICON_DOWN}
              chartData={myTeamStore.skillGapPerformance?.lowRatedSkill || []}
              onItemClick={handleSkillClick}
              isLoading={skillGapPerformanceLoading}
            />
          </AppBox>
        </AppBox>
      </AppBox>

      {/* Skill Distribution & Performance */}
      <AppBox sx={styles.sectionStretch}>
        <AppBox sx={styles.flexItem}>
          <DonutChart
            chartData={myTeamStore.teamDashboardSkillsByRating}
            isLoading={skillsByRatingLoading}
            onRatingChange={rating => myTeamStore.getTeamDashboardSkillsByRating(rating)}
            onItemClick={handleSkillClick}
          />
        </AppBox>
        <AppBox sx={styles.performanceColumn}>
          <PageMetrics
            sx={styles.metricsGrid}
            metrics={skillGapPerformanceMetrics}
            isLoading={skillGapPerformanceLoading}
          />
          <CriticalGapChart
            chartData={myTeamStore.skillGapPerformance?.criticalGapSkills || []}
            isLoading={skillGapPerformanceLoading}
            onThresholdChange={threshold => myTeamStore.getSkillGapPerformance(threshold)}
            onItemClick={handleSkillClick}
          />
        </AppBox>
      </AppBox>

      {/* Used & Unused Skills */}
      <AppBox sx={styles.section}>
        <TeamUsedSkills onItemClick={handleSkillClick} />
        <TeamUnusedSkills />
      </AppBox>
    </Box>
  )
})

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  metricsGrid: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
    gap: 15,
  },
  section: {
    width: '100%',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 15,
  },
  sectionStretch: {
    width: '100%',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 15,
    alignItems: 'stretch',
  },
  chartRow: {
    flex: 1,
    flexDirection: { xs: 'column', md: 'row' },
    gap: 15,
  },
  chartItem: {
    flex: 1,
  },
  flexItem: {
    flex: 1,
  },
  performanceColumn: {
    flex: 1,
    flexDirection: 'column',
    gap: 15,
  },
}
